import * as THREE from 'three';
import { EyeHeight, EYE_ASSUMED } from '../engine/eye-height.js';
import { IntroFade } from '../engine/intro-fade.js';
import { rigPose, type ResolvedView } from '../engine/view.js';
import { Hands } from './hands.js';
import { XrViewer } from './head.js';
import { thumbstickOf } from '../engine/furnishing.js';

export type SelectHandler = (
  ray: THREE.Ray,
  source: 'controller' | 'pointer',
  /**
   * The XR input source that fired, when there is one. Measuring needs this:
   * with two hands tracked, the hit under the hand that pulled the trigger is
   * the one the student meant.
   */
  inputSource?: XRInputSource,
  /** The controller that fired, so a caller can keep re-reading its ray. */
  controller?: THREE.Object3D,
) => void;

/**
 * Lets something claim the pointer for a drag. Returning true suppresses the
 * normal select and the desktop look-around for the duration.
 */
export type GrabHandler = (ray: THREE.Ray, controller?: THREE.Object3D) => boolean;

/**
 * Renderer, camera rig and input, shared by VR, AR and desktop preview.
 *
 * The rig is what moves during the tour; the camera (the user's head) and the
 * controllers live inside it. Everything fixed relative to the viewer -- the
 * lectern, the fade quad -- is parented to the rig or the camera, so it travels
 * with them.
 */
/** How far a pointer ray is drawn when it is not pointing at anything. */
const POINTER_REACH = 6;
/** The camera's far plane: space and the studio fit in 300 m; a world's volcano stands at 260 m. */
export const FAR_PLANE = 300;

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly rig = new THREE.Group();
  readonly controllers: THREE.XRTargetRaySpace[] = [];
  readonly #rayLines: THREE.Line[] = [];
  readonly #cursors: THREE.Object3D[] = [];

  /** The student's own hands, when the headset is tracking them. */
  readonly hands: Hands;

  readonly #fade: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  readonly #raycaster = new THREE.Raycaster();
  readonly #clock = new THREE.Clock();
  /** Seconds run by `settle` on top of the clock's own, so `elapsed` never runs backwards. */
  #settled = 0;
  readonly #frameHandlers = new Set<(dt: number, elapsed: number, frame?: XRFrame) => void>();
  readonly #travelHandlers = new Set<() => void>();
  readonly #clearHandlers = new Set<() => void>();

  #onSelect: SelectHandler | undefined;
  #onGrab: GrabHandler | undefined;
  #onGrabMove: ((ray: THREE.Ray) => void) | undefined;
  #onGrabEnd: (() => void) | undefined;
  #grabbing: THREE.Object3D | 'pointer' | undefined;
  #travel: { fadeOut: boolean; t: number; apply?: () => void } | undefined;
  readonly #intro = new IntroFade();
  #hint: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> | undefined;
  /** What the hint's canvas says now. */
  #hintShown: string | undefined;
  #dragging = false;
  #dragMoved = 0;
  #pointer = { x: 0, y: 0 };
  #yaw = 0;
  #pitch = 0;
  #hoverRay: THREE.Ray | undefined;
  #sessionMode: XRSessionMode | undefined;
  #onPresenting: ((presenting: boolean) => void) | undefined;
  readonly #eyes: EyeHeight;
  readonly #viewer = new XrViewer();
  #onEyeKnown: (() => void) | undefined;

  /**
   * @param previewEye Where the desktop preview's camera stands (`?eye=`), so a
   *   laptop can show what a child sees. A headset uses the pupil's own eyes.
   */
  constructor(
    readonly canvasParent: HTMLElement = document.body,
    previewEye = EYE_ASSUMED,
  ) {
    this.#eyes = new EyeHeight(previewEye);
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.xr.enabled = true;
    this.renderer.xr.setReferenceSpaceType('local-floor');
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // One soft shadow for every lesson that has a sun (ADR-032). On from the
    // start, so no material is compiled twice; drawn only when asked
    // (`redrawShadow`): on load, at a step, and while something moves.
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.shadowMap.autoUpdate = false;
    canvasParent.appendChild(this.renderer.domElement);

    // 85 degrees vertical, not 70. The lectern sits 0.95 m below eye height at
    // 1.9 m (docs/04), which puts its buttons about 36 degrees below the
    // horizon -- outside a 70-degree frame on a laptop. A Quest has roughly 96
    // degrees vertically so the same layout fits there, and WebXR replaces this
    // projection entirely in a session, so a wider angle only affects the
    // desktop preview. Widening the frame keeps the validated lectern geometry
    // instead of moving it to suit a monitor.
    this.camera = new THREE.PerspectiveCamera(
      85,
      window.innerWidth / window.innerHeight,
      0.02,
      FAR_PLANE,
    );
    this.camera.position.set(0, this.#eyes.preview, 0);
    this.rig.add(this.camera);
    this.scene.add(this.rig);

    // Fade quad for comfortable teleports: docs/04 asks for ~0.3 s out and in,
    // never smooth locomotion.
    this.#fade = new THREE.Mesh(
      new THREE.PlaneGeometry(4, 4),
      new THREE.MeshBasicMaterial({
        color: 0x000000,
        transparent: true,
        opacity: 0,
        depthTest: false,
      }),
    );
    this.#fade.position.z = -0.3;
    this.#fade.renderOrder = 999;
    this.camera.add(this.#fade);

    this.#setUpControllers();
    this.#setUpDesktopInput();
    // Built once and left alone: a hand that is not being tracked simply has
    // no joints, so there is nothing to turn on or off when a student puts
    // the controllers down mid-lesson (ADR-025).
    this.hands = new Hands(this.renderer, this.rig);

    window.addEventListener('resize', () => this.#resize());
    this.renderer.setAnimationLoop((_time, frame) => this.#tick(frame));
  }

  onSelect(handler: SelectHandler): void {
    this.#onSelect = handler;
  }

  /**
   * Registers a grab: `claim` decides whether a press starts a drag, `move`
   * receives the ray each frame while it lasts, and `end` fires on release.
   */
  onGrab(claim: GrabHandler, move: (ray: THREE.Ray) => void, end: () => void): void {
    this.#onGrab = claim;
    this.#onGrabMove = move;
    this.#onGrabEnd = end;
  }

  /** Takes the grab away (a lesson ending): a press after this claims nothing, and one held ends. */
  offGrab(): void {
    if (this.#grabbing !== undefined) this.#onGrabEnd?.();
    this.#grabbing = undefined;
    this.#onGrab = undefined;
    this.#onGrabMove = undefined;
    this.#onGrabEnd = undefined;
  }

  get grabbing(): boolean {
    return this.#grabbing !== undefined;
  }

  /** The controller holding the grab now; undefined for the mouse or no grab. */
  get grabController(): THREE.Object3D | undefined {
    return this.#grabbing === 'pointer' ? undefined : this.#grabbing;
  }

  /**
   * A controller's thumbstick, x right and y down (xr-standard axes 2 and 3); zeros for a hand, a
   * controller without a gamepad, or none (ADR-037: turning a carried piece).
   */
  thumbstick(controller: THREE.Object3D | undefined): [number, number] {
    return thumbstickOf(controller?.userData['source'] as XRInputSource | undefined);
  }

  /**
   * Tilts the desktop-preview camera. AR lessons use it because the panel sits
   * at chest height and a person looking at it naturally looks down; without
   * the tilt the lowest answer button falls outside a monitor's frame. The AR
   * prototype did the same. Ignored in a headset, where the device supplies
   * the pose.
   */
  setDesktopPitch(radians: number): void {
    if (this.presenting) return;
    this.#pitch = radians;
    this.camera.rotation.set(this.#pitch, this.#yaw, 0, 'YXZ');
  }

  onFrame(handler: (dt: number, elapsed: number, frame?: XRFrame) => void): () => void {
    this.#frameHandlers.add(handler);
    return () => this.#frameHandlers.delete(handler);
  }

  get presenting(): boolean {
    return this.renderer.xr.isPresenting;
  }

  /**
   * The viewer in the world, to read the head's pose from (position, turn,
   * matrixWorld) in a frame handler or an input event: the preview's camera
   * on a laptop; in a headset a stand-in kept from three's XR camera and the
   * rig (head.ts, XrViewer), this frame's pose wherever the rig was moved.
   *
   * Never read `renderer.xr.getCamera()` for a pose: three keeps it in the
   * reference space, without the rig, from the start of an XR frame until the
   * render, so in a frame handler it is where the pupil stood when the session
   * began -- a VR lesson's tour, a villa room, glow halos, world sound and a
   * carried piece all went wrong that way (2 Oct 2026). head.test.ts holds it.
   *
   * Not the one to hand to render() -- see #tick.
   */
  get activeCamera(): THREE.Camera {
    return this.presenting
      ? this.#viewer.read(this.renderer.xr.getCamera(), this.rig)
      : this.camera;
  }

  /**
   * A teleport: fades to black, calls `arrive` there, and fades back in.
   *
   * `arrive` places the rig (`placeRig`) and the lectern for the destination,
   * computed at the black rather than before it: the eye height is read then,
   * and the lectern is already in place when the view fades in.
   */
  /**
   * Black until `fadeIn`: a lesson's start, while its world loads (ADR-032),
   * or a 360° photo late at a step's black (ADR-036). After 2 s the black
   * says `hint`, "De wereld wordt geladen…" unless told otherwise.
   */
  holdBlack(hint?: string): void {
    this.#intro.hold(hint);
  }

  /** From black to the lesson over `seconds` (1.5 s at a lesson's start). */
  fadeIn(seconds?: number): void {
    this.#intro.start(seconds);
  }

  /** The shadow map is drawn again on the next frame, and only then. */
  redrawShadow(): void {
    this.renderer.shadowMap.needsUpdate = true;
  }

  travelTo(arrive: () => void, instant = false): void {
    if (instant) {
      arrive();
      return;
    }
    this.#travel = { fadeOut: true, t: 0, apply: arrive };
    for (const handler of this.#travelHandlers) handler();
  }

  /**
   * Told once, on the first frame nothing is faded: a teleport's fade-in and
   * a lesson's opening fade both done (Voorlezen reads a step's panel then,
   * not in the dark). Until the next lesson.
   */
  whenClear(handler: () => void): void {
    this.#clearHandlers.add(handler);
  }

  /** Told as a teleport's fade to black begins (the voice-over stops there). Until the next lesson. */
  onTravel(handler: () => void): () => void {
    this.#travelHandlers.add(handler);
    return () => this.#travelHandlers.delete(handler);
  }

  /**
   * Stands the rig for a view: on the floor when the engine framed it, at
   * `pos.y − 1.6` for a lesson's own viewpoint. Its yaw faces the subject
   * without the student having to turn round; the preview's camera also tilts
   * towards it, which a headset never does (docs/04: fade and yaw only).
   */
  placeRig(view: ResolvedView): void {
    const pose = rigPose(view);
    this.rig.position.set(...pose.position);
    this.rig.rotation.y = pose.yaw;
    if (!this.presenting) {
      const eye = pose.position[1] + this.camera.position.y;
      const dx = view.lookAt[0] - pose.position[0];
      const dz = view.lookAt[2] - pose.position[2];
      this.#yaw = 0;
      this.#pitch = Math.atan2(view.lookAt[1] - eye, Math.hypot(dx, dz));
      this.camera.rotation.set(this.#pitch, 0, 0, 'YXZ');
    }
  }

  /**
   * How high the pupil's eyes are above the floor: the headset's pose once it
   * has one (1.6 m until then), the preview's `?eye=` on a laptop.
   */
  get eyeHeight(): number {
    return this.#eyes.current(this.presenting);
  }

  /**
   * Told once per session, the first frame the headset's pose gives the eye
   * height: the view and the lectern were placed for 1.6 m until then, and
   * are placed again without a fade.
   */
  onEyeKnown(handler: () => void): void {
    this.#onEyeKnown = handler;
  }

  /**
   * Stops each pointer ray at what it is pointing at.
   *
   * A ray drawn its full length runs through the cursor and out the other
   * side, which reads as pointing past the button rather than at it. Ending it
   * at the hit means the circle is the end of the line, with nothing behind it.
   *
   * Indexed to match `pointingRays`; `undefined` means that ray hit nothing
   * and goes back to its full length.
   */
  setPointerReach(distances: readonly (number | undefined)[]): void {
    if (!this.presenting) return;
    for (const [index, line] of this.#rayLines.entries()) {
      // A shade short of the surface, so the line does not z-fight the cursor
      // it is meeting.
      const hit = distances[index];
      line.scale.z = hit === undefined ? POINTER_REACH : Math.max(0.05, hit - 0.01);

      // And the cursor itself, on the surface. A ray with nothing at the end
      // of it is a line into space: what tells a student they are pointing
      // *at* something is the mark landing on it, which is the one affordance
      // every headset shares.
      const cursor = this.#cursors[index];
      if (!cursor) continue;
      cursor.visible = hit !== undefined;
      if (hit !== undefined) cursor.position.set(0, 0, -hit);
    }
  }

  /**
   * A small ring on the surface a ray has found.
   *
   * Parented to the controller and moved along its own -z, so it needs no
   * world-space arithmetic and cannot drift out of step with the ray it
   * belongs to. Drawn without depth testing so it is visible against the
   * thing it is touching rather than buried a millimetre inside it.
   */
  #makeCursor(): THREE.Object3D {
    const material = new THREE.MeshBasicMaterial({
      color: 0x9ec5ff,
      transparent: true,
      opacity: 0.9,
      depthTest: false,
    });
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.008, 0.012, 20), material);
    const dot = new THREE.Mesh(new THREE.CircleGeometry(0.003, 12), material);

    const cursor = new THREE.Group();
    cursor.add(ring, dot);
    // Facing back along the ray, so it reads as a mark lying on the surface
    // rather than a disc edge-on to it.
    cursor.rotation.y = Math.PI;
    cursor.renderOrder = 998;
    cursor.visible = false;
    return cursor;
  }

  /**
   * The rays a viewer is currently pointing with: their hands in a headset,
   * the mouse on a laptop.
   *
   * One accessor for both so the preview shows what the headset shows -- the
   * hover state and the cursor were immersive-only, which made them
   * unverifiable without putting a Quest on.
   */
  get pointingRays(): THREE.Ray[] {
    if (this.presenting) return this.controllers.map((controller) => this.rayFrom(controller));
    return this.#hoverRay ? [this.#hoverRay] : [];
  }

  /**
   * Leaves immersive mode, so the browser page is visible again.
   *
   * Nothing else ends the XR session. On a laptop that costs nothing -- there
   * is no session -- but in a headset a lesson that finishes without this
   * leaves the student inside a session with nothing left rendering in it,
   * looking at a frozen view with no way back. The lobby is DOM, and DOM is
   * invisible from inside WebXR.
   */
  /**
   * The kind of session currently running, if any.
   *
   * Tracked rather than read back from the session because WebXR does not
   * expose it, and it decides whether a new lesson can reuse the session the
   * student is already in: `immersive-vr` and `immersive-ar` are different
   * sessions, so a VR lesson following an AR one has to start a new one.
   */
  get sessionMode(): XRSessionMode | undefined {
    return this.#sessionMode;
  }

  /**
   * Told when the student enters or leaves immersive mode.
   *
   * The teacher's grid distinguishes a headset that has loaded the lesson from
   * one that is actually being worn, and nothing but the headset can know
   * which it is.
   */
  onPresentingChange(handler: (presenting: boolean) => void): void {
    this.#onPresenting = handler;
  }

  /** Hands an XR session to the renderer and remembers what kind it is. */
  async enterXr(session: XRSession, mode: XRSessionMode): Promise<void> {
    this.#sessionMode = mode;
    session.addEventListener('end', () => {
      this.#sessionMode = undefined;
      this.#eyes.reset();
      this.#onPresenting?.(false);
    });
    await this.renderer.xr.setSession(session);
    this.#onPresenting?.(true);
  }

  /**
   * Puts the stage back to how a lesson finds it.
   *
   * The stage is reused between lessons so a student in a headset is not
   * thrown out of immersive mode between them, which means everything a
   * lesson changed has to be undone here rather than by throwing the stage
   * away.
   */
  resetForNextLesson(): void {
    this.rig.position.set(0, 0, 0);
    this.rig.rotation.set(0, 0, 0);
    this.camera.position.set(0, this.#eyes.preview, 0);
    this.#yaw = 0;
    this.#pitch = 0;
    this.camera.rotation.set(0, 0, 0, 'YXZ');
    this.#travel = undefined;
    this.#intro.reset();
    this.#fade.material.opacity = 0;
    this.#grabbing = undefined;
    this.#hoverRay = undefined;
    // Handlers belong to the lesson that registered them, not to the stage.
    this.#frameHandlers.clear();
    this.#travelHandlers.clear();
    this.#clearHandlers.clear();
    this.#onSelect = undefined;
    this.#onGrab = undefined;
    this.#onGrabMove = undefined;
    this.#onGrabEnd = undefined;
    this.#onEyeKnown = undefined;
    this.scene.background = null;
    // The whole look goes with the lesson (ADR-032): its sky, its light, its
    // haze and its reach.
    this.scene.backgroundRotation.set(0, 0, 0);
    this.scene.environment = null;
    this.scene.environmentIntensity = 1;
    this.scene.environmentRotation.set(0, 0, 0);
    this.scene.fog = null;
    this.renderer.toneMappingExposure = 1;
    if (this.camera.far !== FAR_PLANE) {
      this.camera.far = FAR_PLANE;
      this.camera.updateProjectionMatrix();
    }
    this.redrawShadow();
  }

  /**
   * The desktop preview's view as it is now, drawn once at `width` × `height` (pixel ratio 1, the
   * same renderer, so the same tone mapping and exposure) into a canvas of its own: "Maak plaatje"
   * (ADR-059). The world only: what travels with the viewer (the lectern, "Sla over", the fade, the
   * controllers and hands, all in the rig or the camera) is hidden for the one draw.
   *
   * Drawn and copied in one task, so the WebGL canvas needs no preserveDrawingBuffer; its size, pixel
   * ratio and the camera's aspect are put back before the task ends, so nothing is ever shown at the
   * picture's size. Never in a headset: there the camera is three's XR camera, not this one.
   *
   * `fov`: the camera's vertical angle for this one draw (a lesson's picture is 60°, ADR-098); the
   * preview's own 85° otherwise.
   */
  still(width: number, height: number, fov?: number): HTMLCanvasElement {
    if (this.presenting)
      throw new Error('Een plaatje maak je in de desktopweergave, niet in de headset');
    const renderer = this.renderer;
    const size = renderer.getSize(new THREE.Vector2());
    const ratio = renderer.getPixelRatio();
    const aspect = this.camera.aspect;
    const angle = this.camera.fov;
    const hidden = [
      ...this.rig.children.filter((child) => child !== this.camera),
      ...this.camera.children,
    ].filter((object) => object.visible);
    for (const object of hidden) object.visible = false;
    try {
      renderer.setPixelRatio(1);
      renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      if (fov !== undefined) this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
      renderer.render(this.scene, this.camera);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Geen 2D-canvas');
      context.drawImage(renderer.domElement, 0, 0, width, height);
      return canvas;
    } finally {
      for (const object of hidden) object.visible = true;
      renderer.setPixelRatio(ratio);
      renderer.setSize(size.x, size.y, false);
      this.camera.aspect = aspect;
      this.camera.fov = angle;
      this.camera.updateProjectionMatrix();
    }
  }

  /** Hidden between lessons, so the lobby is not drawn over an empty scene. */
  set visible(visible: boolean) {
    this.renderer.domElement.style.display = visible ? '' : 'none';
  }

  async exitXr(): Promise<void> {
    const session = this.renderer.xr.getSession();
    if (!session) return;

    // Resolved by the event rather than by end()'s own promise. A session that
    // never resolves it would otherwise hang whatever awaited this, and the
    // student would be left in a frozen view waiting on a promise -- which is
    // worse than the problem being fixed. The event is also what three.js
    // listens for, so by the time this returns the renderer is out too.
    const ended = new Promise<void>((resolve) => {
      session.addEventListener('end', () => resolve(), { once: true });
      // A headset that will not let go is not worth freezing the app over: the
      // student can take it off, and the lobby is waiting when they come back.
      setTimeout(resolve, 3_000);
    });

    try {
      void session.end();
    } catch (error) {
      // Already ending, most likely. Still wait for the event.
      console.warn('could not end the XR session', error);
    }

    await ended;
  }

  /** Anything faded over the view: a teleport, the lesson's opening, a 360° photo held at its black. */
  get faded(): boolean {
    return this.#travel !== undefined || this.#fade.material.opacity > 0 || this.#intro.hint;
  }

  /** The view fully black: a teleport's middle, the lesson's start before it fades in. */
  get black(): boolean {
    return this.#fade.material.opacity >= 1;
  }

  /** True while a teleport fade is in progress; callers suppress input then. */
  get travelling(): boolean {
    return this.#travel !== undefined;
  }

  /**
   * "De wereld wordt geladen…" (or what the hold asked for) on the black, made
   * the first time it is needed: 1.5 m ahead, so both eyes rest on it, drawn
   * over the fade whatever is nearer. Painted again when the text changes.
   */
  #waitHint(text: string): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
    if (!this.#hint) {
      const canvas = document.createElement('canvas');
      canvas.width = 1024;
      canvas.height = 128;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      this.#hint = new THREE.Mesh(
        new THREE.PlaneGeometry(0.6, 0.075),
        new THREE.MeshBasicMaterial({
          map: texture,
          transparent: true,
          depthTest: false,
          toneMapped: false,
          fog: false,
        }),
      );
      this.#hint.position.set(0, -0.05, -1.5);
      this.#hint.renderOrder = 1000;
      this.#hint.visible = false;
      this.camera.add(this.#hint);
    }
    if (this.#hintShown !== text) {
      this.#hintShown = text;
      const texture = this.#hint.material.map as THREE.CanvasTexture;
      const canvas = texture.image;
      const context = canvas.getContext('2d');
      if (context) {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#e8ecf2';
        context.font = '500 64px system-ui, sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(text, 512, 64);
      }
      texture.needsUpdate = true;
    }
    return this.#hint;
  }

  #setUpControllers(): void {
    for (let i = 0; i < 2; i++) {
      const controller = this.renderer.xr.getController(i);

      const ray = new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(),
          new THREE.Vector3(0, 0, -1),
        ]),
        new THREE.LineBasicMaterial({ color: 0x8fd3ff, transparent: true, opacity: 0.5 }),
      );
      ray.scale.z = POINTER_REACH;
      this.#rayLines[i] = ray;
      controller.add(ray);

      const cursor = this.#makeCursor();
      this.#cursors[i] = cursor;
      controller.add(cursor);
      controller.add(
        new THREE.Mesh(
          new THREE.SphereGeometry(0.012, 12, 8),
          new THREE.MeshBasicMaterial({ color: 0x8fd3ff }),
        ),
      );

      // Both controller trigger and hand pinch arrive as 'selectstart', so
      // hand tracking needs no separate path.
      controller.addEventListener('selectstart', () => {
        const ray = this.rayFrom(controller);
        // A grab wins over a select: pointing at the panel and squeezing
        // should move the panel, not place a measuring point behind it.
        if (this.#onGrab?.(ray, controller)) {
          this.#grabbing = controller;
          return;
        }
        this.#onSelect?.(
          ray,
          'controller',
          controller.userData['source'] as XRInputSource | undefined,
          controller,
        );
      });
      controller.addEventListener('selectend', () => {
        if (this.#grabbing === controller) {
          this.#grabbing = undefined;
          this.#onGrabEnd?.();
        }
      });
      controller.addEventListener('connected', (event) => {
        controller.userData['source'] = (event as unknown as { data: XRInputSource }).data;
      });
      controller.addEventListener('disconnected', () => {
        controller.userData['source'] = undefined;
      });

      this.rig.add(controller);
      this.controllers.push(controller);
    }
  }

  rayFrom(controller: THREE.Object3D): THREE.Ray {
    const origin = new THREE.Vector3();
    const direction = new THREE.Vector3(0, 0, -1);
    controller.getWorldPosition(origin);
    direction.applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion()));
    return new THREE.Ray(origin, direction.normalize());
  }

  /** Desktop preview: drag to look, click to select, arrow keys for steps. */
  #setUpDesktopInput(): void {
    const element = this.renderer.domElement;

    element.addEventListener('pointerdown', (event) => {
      this.#pointer = { x: event.clientX, y: event.clientY };
      this.#dragMoved = 0;

      if (!this.presenting && this.#onGrab?.(this.#rayFromPointer(event), undefined)) {
        this.#grabbing = 'pointer';
        return;
      }
      this.#dragging = true;
    });

    element.addEventListener('pointermove', (event) => {
      // Remembered whether or not a drag is in progress, so the desktop
      // preview can show the same hover and cursor a headset does.
      this.#hoverRay = this.#rayFromPointer(event);

      if (this.#grabbing === 'pointer') {
        this.#onGrabMove?.(this.#rayFromPointer(event));
        return;
      }
      if (!this.#dragging || this.presenting) return;
      const dx = event.clientX - this.#pointer.x;
      const dy = event.clientY - this.#pointer.y;
      this.#dragMoved += Math.abs(dx) + Math.abs(dy);
      this.#yaw -= dx * 0.004;
      this.#pitch = Math.max(-1.2, Math.min(1.2, this.#pitch - dy * 0.004));
      this.#pointer = { x: event.clientX, y: event.clientY };
      this.camera.rotation.set(this.#pitch, this.#yaw, 0, 'YXZ');
    });

    window.addEventListener('pointerup', (event) => {
      if (this.#grabbing === 'pointer') {
        this.#grabbing = undefined;
        this.#onGrabEnd?.();
        return;
      }
      const wasDragging = this.#dragging;
      this.#dragging = false;
      if (!wasDragging || this.presenting || this.#dragMoved > 4) return;

      this.#onSelect?.(this.#rayFromPointer(event), 'pointer');
    });
  }

  #rayFromPointer(event: { clientX: number; clientY: number }): THREE.Ray {
    const ndc = new THREE.Vector2(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1,
    );
    this.#raycaster.setFromCamera(ndc, this.camera);
    return this.#raycaster.ray.clone();
  }

  #resize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  }

  /**
   * Runs `seconds` of the lesson's frames at once, without drawing them: a teleport's fade, the
   * opening fade, the models' and shapes' changes all move on as they would over that time, at
   * 30 frames a second. For a lesson's picture (ADR-098), drawn in a page the browser may not give
   * animation frames to (an iframe out of sight), so the scene is settled the same way every time.
   * Never in a headset.
   */
  settle(seconds: number): void {
    if (this.presenting) return;
    const dt = 1 / 30;
    for (let t = 0; t < seconds; t += dt) {
      this.#settled += dt;
      this.#advance(dt, this.#clock.elapsedTime + this.#settled);
    }
  }

  #tick(frame?: XRFrame): void {
    const dt = Math.min(this.#clock.getDelta(), 0.05);
    const elapsed = this.#clock.elapsedTime + this.#settled;
    this.#advance(dt, elapsed, frame);

    // Always our own camera, never activeCamera. While presenting, three.js
    // calls xr.updateCamera() with whatever is passed here and then swaps in
    // the XR camera itself -- and updateCamera derives the headset's world
    // position from that camera's PARENT. The XR camera has no parent, so
    // passing it skipped the rig entirely and the viewpoint stayed at the
    // origin however far travelTo moved: the tour worked in desktop preview,
    // where activeCamera is the rig's child, and did nothing in a headset.
    this.renderer.render(this.scene, this.camera);
    // Three's XR camera is in the world now, until the next frame.
    this.#viewer.rendered();
  }

  /** One frame's worth of everything but the drawing. */
  #advance(dt: number, elapsed: number, frame?: XRFrame): void {
    // The headset's pose in the local-floor space this frame. Three has just
    // set its XR camera to it, without the rig, when there is one: the
    // viewer (activeCamera) adds the rig until the render.
    const space = frame ? this.renderer.xr.getReferenceSpace() : null;
    const pose = frame && space ? frame.getViewerPose(space) : undefined;
    this.#viewer.frameStarted(pose !== undefined && pose !== null);

    // First, so anything reading a hand this frame reads this frame's hand.
    this.hands.update();

    // The eyes' height above the real floor, from the headset's own pose in
    // the local-floor space; moving the rig does not change it. Not from a
    // pose whose position is emulated (tracking lost, or not yet found): that
    // height is a guess, not the pupil.
    if (pose && !pose.emulatedPosition && this.#eyes.observe(pose.transform.position.y)) {
      this.#onEyeKnown?.();
    }

    let travelBlack = 0;
    if (this.#travel) {
      // ~0.3 s each way, matching the prototypes.
      const speed = 3;
      this.#travel.t += dt * speed * (this.#travel.fadeOut ? 1 : -1);
      travelBlack = Math.max(0, Math.min(1, this.#travel.t));

      if (this.#travel.fadeOut && this.#travel.t >= 1) {
        this.#travel.apply?.();
        this.#travel = { fadeOut: false, t: 1 };
      } else if (!this.#travel.fadeOut && this.#travel.t <= 0) {
        travelBlack = 0;
        this.#travel = undefined;
      }
    }
    // The darker of a teleport's fade and a lesson's start.
    this.#fade.material.opacity = Math.max(travelBlack, this.#intro.update(dt));
    if (this.#clearHandlers.size > 0 && this.#fade.material.opacity === 0 && !this.#travel) {
      const handlers = [...this.#clearHandlers];
      this.#clearHandlers.clear();
      for (const handler of handlers) handler();
    }
    // A world taking its time: say so, rather than leave a class in the dark.
    if (this.#intro.hint) this.#waitHint(this.#intro.hintText).visible = true;
    else if (this.#hint) this.#hint.visible = false;

    if (this.#grabbing && this.#grabbing !== 'pointer') {
      this.#onGrabMove?.(this.rayFrom(this.#grabbing));
    }

    for (const handler of this.#frameHandlers) handler(dt, elapsed, frame);
  }
}
